import { computed } from 'vue';

import { useCompanyStore } from '@tallo/investor/company';
import { useInvestorStore } from '@tallo/investor/investor';
import { useCodeboxIntegrationQuery, useCodeboxesQuery } from '@tallo/investor/property';
import { CalendarProvider } from '@tallo/user';

export interface IntegrationStatus {
  connected: boolean;
  /** The account or scope shown next to the Connected badge. */
  meta?: string;
}

/**
 * Each integration keeps its own source of truth, so the marketplace joins them rather than
 * introducing a fourth place that could disagree with the three detail flows.
 */
export function useIntegrationStatus() {
  const investorStore = useInvestorStore();
  const companyStore = useCompanyStore();

  const codeboxIntegration = useCodeboxIntegrationQuery();
  const isCodeboxConnected = computed(() => Boolean(codeboxIntegration.data.value?.connected));
  const codeboxes = useCodeboxesQuery(() => isCodeboxConnected.value);

  const googleCalendar = computed(() =>
    investorStore.investor?.user?.calendars?.find((calendar) => calendar.provider === CalendarProvider.GOOGLE),
  );

  // Only the very first load blanks the page; later refetches keep the current cards on screen.
  const isLoading = computed(() => codeboxIntegration.isPending.value || companyStore.isLoading);

  const statusById = computed<Record<string, IntegrationStatus>>(() => {
    const lockboxCount = codeboxes.data.value?.length ?? 0;

    return {
      'google-calendar': {
        connected: Boolean(googleCalendar.value),
        meta: googleCalendar.value?.userEmail,
      },
      aptly: {
        connected: Boolean(companyStore.isAptlyConnected),
        meta: companyStore.isAptlyConnected ? 'Listing feed' : undefined,
      },
      codebox: {
        connected: isCodeboxConnected.value,
        meta: lockboxCount ? `${lockboxCount} lockbox${lockboxCount === 1 ? '' : 'es'}` : undefined,
      },
    };
  });

  const connectedCount = computed(() => Object.values(statusById.value).filter((status) => status.connected).length);

  function statusOf(integrationId: string): IntegrationStatus {
    return statusById.value[integrationId] ?? { connected: false };
  }

  return { statusOf, connectedCount, isLoading };
}
